"use client"

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { CloseLabelProvider } from "@quibas/integration-hub-ui/close-label"
import { formatDate, isLocale, localeCookie, translate, translateMessage, type Locale } from "./locale"
import type { MessageKey } from "./messages"

function presentation(locale: Locale) {
  return {
    locale,
    t: (key: MessageKey, values?: Record<string, string | number>) => translate(locale, key, values),
    message: (value: string) => translateMessage(locale, value),
    date: (value: string) => formatDate(locale, value),
  }
}
const Context = createContext({ ...presentation("tr"), setLocale: (_value: Locale) => {} })

export function LocaleProvider({ initialLocale, children }: { initialLocale: Locale; children: ReactNode }) {
  const [locale, updateLocale] = useState(initialLocale)
  useEffect(() => { document.documentElement.lang = locale }, [locale])
  const value = useMemo(() => ({ ...presentation(locale), setLocale: (next: Locale) => {
    if (!isLocale(next)) return
    // Presentation preference only: no identity, tenant, permission, or redirect data.
    document.cookie = `${localeCookie}=${next}; Path=/; Max-Age=31536000; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`
    updateLocale(next)
  } }), [locale])
  // Shared dialogs and sheets announce their close button in the interface language.
  return <Context.Provider value={value}><CloseLabelProvider label={value.t("Kapat")}>{children}</CloseLabelProvider></Context.Provider>
}
export function useLocale() { return useContext(Context) }
